import React from 'react';
import { VictoryChart, VictoryBar, VictoryAxis, VictoryLabel } from 'victory';
import { computeHistogram } from '../../../utils/histogram';

// BarChart is the Histogram Report's chart, and only that — ReportHg.js reaches it through its
// `default:` case and ReportDataChartPdf.js through its fallback branch. Binning lives here so the
// app view, the PDF export and the sample-data preview cannot drift apart again.
class BarChart extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      clicked: false,
      style: {
        data: { fill: "tomato" }
      }
    };
  }

  handleMouseOver = () => {
    const fillColor = this.state.clicked ? "blue" : "tomato";
    const clicked = !this.state.clicked;
    this.setState({
      clicked,
      style: {
        data: { fill: fillColor }
      }
    });
  };

  // 16, not 10: a bin label like "49.95-49.97" is 11 chars and truncating it to "49.95-49.9..."
  // renders a wrong number rather than a shortened one.
  truncateLabel = (label) => {
    const text = String(label ?? '');
    return text.length > 16 ? `${text.substring(0, 16)}...` : text;
  };

  render() {
    const { data, isPdfView, maxWidth } = this.props;

    // Always bin. `text` is never a bin range in practice — the old Add-Data form offered a free-text
    // Label beside the Value and real reports hold junk in it ("asdasd", or the value repeated), so
    // branching on it would leave those reports rendering raw values as bar heights.
    const histogram = computeHistogram((data || []).map((d) => d.value));
    if (!histogram) return null;

    // Position bars by bin index, not by label. Whole-number rounding makes narrow data produce
    // repeated labels ("50-50"), and a categorical x keyed on the label would collapse every repeat
    // onto one x-slot — 10 bins rendering as 3 overlapping bars. Index keeps each bin its own bar; the
    // axis below maps the index back to the (possibly repeated) label. No `label` field here — Victory
    // would render it as the bar-top text, hiding the frequency count.
    const bars = histogram.bins.map((b, i) => ({ x: i, y: b.count }));

    // The PDF is printed, not scrolled, so anything past the card's content box is cut off rather
    // than reachable: 10 bins at 95px each (950px) lost its right-hand bins inside
    // ReportDataChartPdf's ~920px card. Cap to the container there — the same trade RunChart already
    // makes for its PDF view.
    const naturalWidth = Math.max(bars.length * (125 - (Math.min(Math.max(bars.length - 3, 0), 3) * 10)), 800);
    const chartWidth = isPdfView && maxWidth ? Math.min(naturalWidth, maxWidth) : naturalWidth;

    // Frequencies are counts, so the axis is whole numbers. Victory's automatic ticks label a small
    // max with decimals ("1.0" ... "7.0" for a max of 7), and rounding them afterwards would repeat
    // values (1.5 and 2 both print "2"), so pick the integer ticks up front.
    const maxCount = Math.max(...bars.map((b) => b.y));
    const tickStep = Math.max(1, Math.ceil(maxCount / 8));
    const countTicks = [];
    for (let t = 0; t <= maxCount; t += tickStep) countTicks.push(t);

    return (
      <div style={{ position: 'relative', width: '100%', overflowX: isPdfView ? 'hidden' : 'auto' }}>
        <div style={{ minWidth: isPdfView ? 'auto' : chartWidth, position: 'relative' }}>
          <VictoryChart 
            height={400} 
            width={chartWidth} 
            padding={{ top: 40, bottom: 50, left: 70, right: 30 }}
            domainPadding={{ x: 50, y: [0, 0] }}
          >
            {/* Left padding carries the y-axis title, which is Victory's own axis label below rather
                than the absolutely-positioned div it used to be — that div sat against the wrapper
                (left: 10px, top: 50%) and so covered the tick numbers in the printed PDF.
                One tick per bin index, formatted from the bins array. Keyed on index (not the label)
                so repeated whole-number labels still get their own tick — the bar and its tick share
                the same index, so they cannot drift apart. */}
            <VictoryAxis
              tickValues={bars.map((b) => b.x)}
              tickFormat={(t) => this.truncateLabel(histogram.bins[t] && histogram.bins[t].label)}
              style={{
                tickLabels: { fontSize: 10, padding: 5 }
              }}
            />
            <VictoryAxis
              dependentAxis
              domain={[0, maxCount]}
              tickValues={countTicks}
              label="Frequency/Values(Units)"
              style={{
                grid: {
                  stroke: "#ccc",
                  strokeWidth: 0.5
                },
                tickLabels: { fontSize: 10 },
                axisLabel: { padding: 45, fontSize: 12 }
              }}
            />
            <VictoryBar
              // A bar is now an aggregate of N observations with no single ChartData row behind it,
              // so it is not an edit target. Editing lives in the table below the chart.
              events={[]}
              style={this.state.style}
              data={bars}
              x="x"
              y="y"
              labels={({ datum }) => datum.y}
              labelComponent={<VictoryLabel dy={-20} />}
            />
          </VictoryChart>
        </div>

        {/* Static caption, not a modal: it renders identically in the app view, the PDF export and the
            sample preview (all three share this component), where an interactive popover would be dead
            in the printed PDF. States the rounding and how bins are derived. */}
        <p style={{ fontSize: 11, color: '#6c757d', marginTop: 8, marginBottom: 0 }}>
          Bins are calculated automatically: bin count = data points ÷ 2, bin width = (max − min) ÷ bin
          count. Bin ranges are rounded to the nearest whole number.
        </p>
      </div>
    );
  }
}

export default BarChart;
